import React, { useState, useEffect, useRef } from 'react';
import { searchUsersByUsername } from '../services/mentionService';

function MentionSuggestions({ 
  query, 
  position, 
  onSelect, 
  onClose,
  isVisible = true 
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const suggestionRef = useRef(null);

  // Fetch user suggestions based on query
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!query || query.trim() === '' || query.length < 1) {
        setSuggestions([]);
        return;
      }
      
      setLoading(true);
      try {
        const users = await searchUsersByUsername(query, 8);
        setSuggestions(users);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error fetching user suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Debounce the search
    const debounceTimer = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(debounceTimer);
  }, [query]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible || !suggestions.length) return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % suggestions.length);
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
          break;
          
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (suggestions[selectedIndex]) {
            onSelect(suggestions[selectedIndex]);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
          
        default:
          break;
      }
    };
    
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [suggestions, selectedIndex, onSelect, onClose, isVisible]);
  
  // Scroll selected item into view
  useEffect(() => {
    if (suggestionRef.current && suggestions.length > 0) {
      const selectedElement = suggestionRef.current.querySelector(`.mention-item.selected`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, suggestions]);
  
  // Handle clicks outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [onClose, isVisible]);
  
  // If not visible or no results to show, don't render
  if (!isVisible || (suggestions.length === 0 && !loading && (!query || query.length < 2))) {
    return null;
  }
  
  return (
    <div 
      className="mention-suggestions" 
      ref={suggestionRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999
      }}
    >
      {loading ? (
        <div className="mention-suggestion-item loading">
          <div className="mention-loading-spinner"></div>
          <span>Searching users...</span>
        </div>
      ) : suggestions.length > 0 ? (
        <>
          <div className="mention-suggestions-header">
            <span className="mention-suggestions-count">
              {suggestions.length} user{suggestions.length !== 1 ? 's' : ''} found
            </span>
          </div>
          {suggestions.map((user, index) => (
            <div 
              key={user.uid}
              className={`mention-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => onSelect(user)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="mention-avatar">
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.displayName || 'User'} />
                ) : (
                  <div className="mention-avatar-placeholder">
                    {(user.displayName || user.username || 'A')[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div className="mention-user-info">
                <div className="mention-name">{user.displayName || user.username}</div>
                <div className="mention-username">@{user.username}</div>
              </div>
            </div>
          ))}
          <div className="mention-suggestions-footer">
            <span className="mention-navigation-hint">
              ↑↓ navigate • ⏎ select • esc close
            </span>
          </div>
        </>
      ) : query && query.length >= 2 ? (
        <div className="mention-suggestion-item no-results">
          <span>No users found for "@{query}"</span>
        </div>
      ) : (
        <div className="mention-suggestion-item hint">
          <span>Type at least 2 characters to search users</span>
        </div>
      )}
    </div>
  );
}

export default MentionSuggestions;